import { useState, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import FuturizerLogo from './FuturizerLogo';
import PageLoader from './PageLoader';

// Left panel embeds the marketing site's hero section live (via iframe)
// instead of a hand-copied headline/description/chips — so it can never
// drift out of sync with enable-dev.com. Only the hero (logo, headline,
// description, chips) is shown, not the whole marketing page: we render the
// iframe at the desktop layout width the crop below was measured at, shift
// it up to clip off the nav bar, then scale the whole thing down with a CSS
// transform to fit whatever width the panel actually has.
const MARKETING_SITE_URL = 'https://enable-dev.com/futurize-pb/?access=fpb-2026-preview-x7k9m';

// Crop geometry, measured against the live page's hero <section> at a
// 1107px-wide viewport: nav bar (68px, floats over the hero) is clipped off
// the top, and the crop runs to the bottom of the hero section itself so we
// stop before the next section on the page begins.
const HERO_SRC_WIDTH      = 1107;
const HERO_CROP_TOP       = 68;
const HERO_CROP_HEIGHT    = 788;
const HERO_IFRAME_HEIGHT  = 1000; // taller than the crop; overflow:hidden on the wrapper clips the excess

// Renders the marketing site's hero section, cropped to exclude its nav bar,
// scaled to fit its wrapper's width. Deliberately fit-width rather than
// "cover" (fill both axes): covering both axes would crop the sides off the
// headline text whenever the panel is taller than the source's own aspect
// ratio. The wrapper is a normal top-anchored block (not vertically
// centered) so any leftover vertical space falls below the content, not
// above it as a blank gap.
function HeroEmbed({ onLoad }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / HERO_SRC_WIDTH);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} style={{ ...styles.heroEmbedWrap, height: HERO_CROP_HEIGHT * scale }}>
      <iframe
        title="Futurize PB — Enable Development"
        src={MARKETING_SITE_URL}
        onLoad={onLoad}
        scrolling="no"
        tabIndex={-1}
        style={{
          width: HERO_SRC_WIDTH,
          height: HERO_IFRAME_HEIGHT,
          border: 'none',
          pointerEvents: 'none',
          transformOrigin: 'top left',
          transform: `scale(${scale}) translateY(-${HERO_CROP_TOP}px)`,
        }}
      />
    </div>
  );
}

const MAX_ATTEMPTS     = 3;
const LOCK_DURATION_SECS = 30;          // increase this value to extend lockout
const LOCK_KEY         = 'futurizer_account_lock';

function getActiveLock(identifier) {
  try {
    const lock = JSON.parse(localStorage.getItem(LOCK_KEY));
    if (lock && lock.identifier === identifier.trim().toLowerCase() && lock.unlocksAt > Date.now()) return lock;
  } catch {}
  return null;
}

function saveLock(identifier) {
  localStorage.setItem(LOCK_KEY, JSON.stringify({
    identifier: identifier.trim().toLowerCase(),
    unlocksAt: Date.now() + LOCK_DURATION_SECS * 1000,
    totalSecs: LOCK_DURATION_SECS,
  }));
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate   = useNavigate();

  const [identifier,   setIdentifier]   = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [attempts,     setAttempts]     = useState(0);
  const [showTerms,    setShowTerms]    = useState(false);
  const [showPrivacy,  setShowPrivacy]  = useState(false);
  const [heroLoaded,   setHeroLoaded]   = useState(false);

  const locked = attempts >= MAX_ATTEMPTS;

  async function handleSubmit(e) {
    e.preventDefault();
    if (locked) return;
    setError('');

    if (!identifier || !password) {
      setError('Please enter your email/username and password.');
      return;
    }

    const activeLock = getActiveLock(identifier);
    if (activeLock) {
      const secs = Math.ceil((activeLock.unlocksAt - Date.now()) / 1000);
      setError(`Account locked. Try again in ${secs} second${secs === 1 ? '' : 's'}.`);
      return;
    }

    setLoading(true);
    try {
      await login(identifier, password);
      setIdentifier('');
      setPassword('');
      setShowPassword(false);
      setError('');
      setAttempts(0);
      navigate('/projects', { replace: true });
    } catch (err) {
      const next = attempts + 1;
      setAttempts(next);
      setIdentifier('');
      setPassword('');
      setShowPassword(false);
      if (next >= MAX_ATTEMPTS) {
        saveLock(identifier);
        navigate('/locked', { replace: true });
      } else {
        setError(`${err.message} (${MAX_ATTEMPTS - next} attempt${MAX_ATTEMPTS - next === 1 ? '' : 's'} remaining)`);
      }
    } finally {
      setLoading(false);
    }
  }

  const TERMS_SECTIONS = [
    { title: '1. Introduction', body: 'These terms govern use of the website and transactions related to products and services. Additional contracts may apply and will take precedence if they conflict with these terms.' },
    { title: '2. Binding', body: 'By accessing or using the website, you agree to be bound by these terms. The mere use of this website implies the knowledge and acceptance of these Terms.' },
    { title: '3. Intellectual Property', body: 'Enable Development and its licensors control all copyright and intellectual property rights on the website. You will not use, copy, reproduce, perform, display, or distribute any resources without prior written permission.' },
    { title: '4. Third-Party Property', body: "The site may link to external websites. Enable Development doesn't monitor or endorse third-party content and isn't responsible for their privacy practices or any resulting losses." },
    { title: '5. Responsible Use', body: 'Users must not distribute malicious software, collect data for direct marketing, or conduct automated data collection. Activities that damage or interfere with website performance are prohibited.' },
    { title: '6. Idea Submission', body: 'Unsolicited ideas submitted without a prior agreement grant Enable Development a worldwide, irrevocable, non-exclusive, royalty-free license to use, reproduce, store, adapt, publish, translate, and distribute.' },
    { title: '7. Termination of Use', body: 'Enable Development may modify or discontinue website access without liability or compensation.' },
    { title: '8. Warranties and Liability', body: 'The website is provided "as is" without warranties. Enable Development disclaims responsibility for availability, accuracy, or completeness. Liability is limited to amounts paid for services.' },
    { title: '9. Privacy', body: 'Users must provide accurate information. See the Privacy Statement and Cookie Policy for details.' },
    { title: '10. Accessibility', body: 'Enable Development commits to accessibility and requests notice of disability-related access issues.' },
    { title: '11. Export Restrictions', body: 'Access is prohibited from territories where products or services are illegal and violates Estonian export laws.' },
    { title: '12. Assignment', body: 'Users cannot assign rights without written consent.' },
    { title: '13. Breaches', body: 'Enable Development may suspend access or pursue legal action for violations.' },
    { title: '14. Force Majeure', body: "Parties aren't liable for delays from causes beyond reasonable control." },
    { title: '15. Indemnification', body: 'Users must reimburse Enable Development for claims arising from term violations or applicable laws.' },
    { title: '16. Waiver', body: 'Failure to enforce provisions does not waive them.' },
    { title: '17. Language', body: 'Terms are interpreted exclusively in English.' },
    { title: '18. Entire Agreement', body: 'These terms, privacy statement, and cookie policy constitute the complete agreement.' },
    { title: '19. Updates', body: 'Enable Development may update terms periodically. Continued use indicates acceptance.' },
    { title: '20. Choice of Law', body: 'Estonian law governs; disputes fall under Estonian court jurisdiction.' },
    { title: '21. Contact', body: 'Enable Development OÜ — info@enable-dev.com — Õismäe tee 16-69, Tallinn 10613, Estonia' },
  ];

  const PRIVACY_SECTIONS = [
    { title: '1. Introduction', body: "Enable Development's website uses cookies and related technologies, some placed by third parties. This policy applies to EU citizens and EEA/Switzerland residents." },
    { title: '2. What are cookies?', body: 'A cookie is a small file sent along with website pages and stored by your browser on your device\'s hard drive.' },
    { title: '3. What are scripts?', body: 'Program code that enables website functionality and interactivity, executed on servers or user devices.' },
    { title: '4. What is a web beacon?', body: 'A small, invisible piece of text or image on a website used to monitor traffic (also called a pixel tag).' },
    { title: '5. Cookie Categories', body: 'Technical/Functional Cookies: Enable core website functions and remember user preferences; no consent required.\n\nStatistics Cookies: Optimize user experience through usage insights; requires user permission.\n\nMarketing/Tracking Cookies: Create user profiles for advertising or cross-website tracking.\n\nSocial Media (LinkedIn): Allows content sharing; may store data for personalized advertising.' },
    { title: '6. Services Used', body: 'WordPress, Google reCAPTCHA, Google Analytics, Complianz, Google Fonts, Google Maps, YouTube, and LinkedIn.' },
    { title: '7. Consent', body: 'Users consent by clicking "Save preferences" on the initial pop-up. Settings can be managed afterward.' },
    { title: '8. Enabling/Disabling Cookies', body: 'Cookies can be managed via browser settings, though disabling them may affect website functionality.' },
    { title: '9. User Rights', body: 'Users may request data access, correction, deletion, portability, and can object to processing. Complaints may be filed with data protection authorities.' },
    { title: '10. Contact', body: 'Enable Development OÜ — Tallinn, Estonia — info@enable-dev.com' },
  ];

  function LegalModal({ title, updated, sections, onClose }) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <div>
              <div style={styles.modalTitle}>{title}</div>
              <div style={styles.modalUpdated}>{updated}</div>
            </div>
            <button style={styles.modalClose} onClick={onClose}>✕</button>
          </div>
          <div style={styles.modalBody}>
            {sections.map(s => (
              <div key={s.title} style={styles.modalSection}>
                <div style={styles.modalSectionTitle}>{s.title}</div>
                <div style={styles.modalSectionBody}>{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {showTerms && (
        <LegalModal
          title="Terms and Conditions"
          updated="Last Updated: June 14, 2024"
          sections={TERMS_SECTIONS}
          onClose={() => setShowTerms(false)}
        />
      )}
      {showPrivacy && (
        <LegalModal
          title="Cookie & Privacy Policy"
          updated="Last Updated: May 15, 2026"
          sections={PRIVACY_SECTIONS}
          onClose={() => setShowPrivacy(false)}
        />
      )}

      {/* ── Left hero panel — just the marketing site's hero section,
          embedded live and cropped to exclude its nav bar (see HeroEmbed
          above), so this stays in sync with enable-dev.com automatically. */}
      <div style={styles.left} className="login-left">
        {/* Label omitted — PageLoader's label text is styled dark-on-light
            and would be unreadable against this panel's dark background. */}
        {!heroLoaded && (
          <div style={styles.heroLoader}>
            <PageLoader label={null} minHeight={0} />
          </div>
        )}

        <HeroEmbed onLoad={() => setHeroLoaded(true)} />
      </div>

      {/* ── Right login panel ─────────────────────────────────── */}
      <div style={styles.right}>
        <div style={styles.rightLogoRow}>
          <FuturizerLogo size={28} variant="full" />
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeading}>
            <h2 style={styles.cardTitle}>Welcome back</h2>
            <p style={styles.cardSub}>Sign in to continue to Futurizer</p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="error-alert"
              style={{ marginBottom: 'var(--space-md)', borderRadius: 'var(--radius-sm)' }}
            >
              <span>{error}</span>
              <button className="alert-close" onClick={() => setError('')}>×</button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-field" style={{ marginBottom: 'var(--space-md)' }}>
              <label htmlFor="identifier">Email or Username</label>
              <input
                id="identifier"
                type="text"
                autoComplete="username"
                placeholder="Email or username"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                disabled={locked}
              />
            </div>

            <div className="form-field" style={{ marginBottom: 'var(--space-lg)' }}>
              <label htmlFor="password">Password</label>
              <div style={styles.passwordWrap}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ paddingRight: 42 }}
                  disabled={locked}
                />
                <button
                  type="button"
                  style={styles.eyeBtn}
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={locked}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {/* Attempt indicator */}
            {attempts > 0 && (
              <div style={styles.attemptDots}>
                {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
                  <span
                    key={i}
                    style={{
                      ...styles.dot,
                      background: i < attempts ? 'var(--error)' : 'var(--border)',
                    }}
                  />
                ))}
                <span style={styles.attemptText}>
                  {MAX_ATTEMPTS - attempts} attempt{MAX_ATTEMPTS - attempts === 1 ? '' : 's'} remaining
                </span>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={styles.submitBtn}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" style={styles.inlineSpinner} />
                  Signing in…
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

        </div>

        <p style={styles.footerNote}>
          © {new Date().getFullYear()} Futurizer · Enable Development OÜ &nbsp;·&nbsp;{' '}
          <button style={styles.footerLink} onClick={() => setShowPrivacy(true)}>Privacy</button>
          &nbsp;·&nbsp;
          <button style={styles.footerLink} onClick={() => setShowTerms(true)}>Terms</button>
        </p>
      </div>
    </div>
  );
}


const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: 'var(--font-family)',
  },
  left: {
    flex: '0 0 62%',
    background: '#0a1420',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
  },
  // Shown over the left panel until the embedded hero loads, so the panel
  // isn't a blank dark rectangle while the iframe fetches.
  heroLoader: {
    position: 'absolute', inset: 0, zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0a1420',
  },
  // Width fills the panel; height is set by HeroEmbed to match the scaled
  // crop. Normal top-anchored block — no vertical centering — so any
  // leftover space falls below the content, not above it as a blank gap.
  heroEmbedWrap: { width: '100%', overflow: 'hidden' },
  right: {
    flex: 1, background: 'var(--bg-page)', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(24px, 5vw, 48px) clamp(16px, 4vw, 32px)', position: 'relative',
  },
  rightLogoRow: { position: 'absolute', top: 28, left: 36 },
  card: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-modal)',
    padding: 'clamp(24px, 5vw, 40px) clamp(20px, 5vw, 40px) clamp(20px, 4vw, 32px)',
    width: '100%', maxWidth: 440,
  },
  cardHeading: { marginBottom: 28 },
  cardTitle: {
    fontSize: 'var(--font-size-2xl)', fontWeight: 800,
    color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.5px',
  },
  cardSub: { fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', margin: 0 },
  passwordWrap: { position: 'relative' },
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 16, lineHeight: 1, padding: 0, opacity: 0.55,
  },
  attemptDots: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginBottom: 'var(--space-md)', marginTop: -8,
  },
  dot: { width: 8, height: 8, borderRadius: '50%', transition: 'background 0.2s' },
  attemptText: { fontSize: 'var(--font-size-xs)', color: 'var(--error)', fontWeight: 500 },
  submitBtn: {
    width: '100%', justifyContent: 'center',
    padding: '11px var(--space-md)', fontSize: 'var(--font-size-md)',
    fontWeight: 600, borderRadius: 'var(--radius-sm)', gap: 8,
  },
  inlineSpinner: { width: 16, height: 16, borderWidth: 2 },
  footerNote: { marginTop: 28, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textAlign: 'center' },
  footerLink: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: 'inherit', fontSize: 'inherit', fontFamily: 'inherit',
    textDecoration: 'underline', textUnderlineOffset: 2,
  },

  // ── Legal modals ──────────────────────────────────────────────────
  modalOverlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px',
  },
  modalBox: {
    background: '#fff', borderRadius: 16,
    boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
    width: '100%', maxWidth: 620,
    maxHeight: '80vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '20px 24px 16px', borderBottom: '1px solid #f0e8e0', flexShrink: 0,
  },
  modalTitle: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', marginBottom: 2 },
  modalUpdated: { fontSize: 11, color: '#888', fontWeight: 500 },
  modalClose: {
    background: 'none', border: '1px solid #e0d8d0', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, color: '#666',
    padding: '4px 10px', lineHeight: 1, flexShrink: 0, marginLeft: 12,
  },
  modalBody: { overflowY: 'auto', padding: '8px 24px 24px' },
  modalSection: { paddingTop: 18 },
  modalSectionTitle: {
    fontSize: 13, fontWeight: 700, color: 'var(--primary)',
    marginBottom: 5, letterSpacing: '0.1px',
  },
  modalSectionBody: {
    fontSize: 13, color: '#444', lineHeight: 1.75,
    whiteSpace: 'pre-line',
  },
};
